import styled from '@emotion/styled';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spacings, mediaQuery } from '@styles/index';
import CourseCarouselSkeleton from '@skeletons/CourseCard/CourseCarousel';
import Typography from 'aquilla/core/atoms/Typography';
import BottomChevron from 'aquilla/icons/actions/BottomChevron';
import NewCourseCard from '@cont/EnrolledCourse/NewCourseCard';
import { useRouter } from 'next/router';
import getParamsFromURL from '@utils/getParamsFromURL';
import useLiveClassAndCourseList from '@api/hooks/educator/useLiveClassAndCourseList';
import isEmptyObject from '@utils/isEmptyObject';
import EducatorEmptyCourse from './EducatorEmptyCourse';
import FiltersTab, { getFiltersApplied } from './FiltersTab';
import useInfiniteFetchNew from './hooks/useInfiniteFetchNew';

const Container = styled.div`
  margin-bottom: -${Spacings.SPACING_10B};
  border-bottom: 1px solid var(--color-divider);
`;
const InnerContainer = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacings.SPACING_10B} ${Spacings.SPACING_4B};
  padding: 0 0 ${Spacings.SPACING_10B} 0;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
  }
`;
const StyledCourseCard = styled(NewCourseCard)`
  width: 272px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: unset;

    > div:nth-child(2) {
      height: unset;
      > img {
        height: auto;
        max-width: unset;
      }
    }
  }
`;
const StyledViewMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 100%;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B} ${Spacings.SPACING_2B}
    ${Spacings.SPACING_4B};
`;
const StyledViewMoreButton = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} ${Spacings.SPACING_1B}
    ${Spacings.SPACING_2B};
`;
const StyledChevronWrapper = styled.span`
  margin-top: 5px;
`;

const useCoursesByFilters = (
  { userName },
  { filtersApplied },
  options = { fallbackData: undefined }
) => {
  const filterParams = {
    username: userName.toLowerCase(),
    ...filtersApplied
  };
  const key = 'sheldon/v1/list/course';
  const infiniteResponse = useInfiniteFetchNew(
    key,
    { applyOffsetsAutomatically: false, params: filterParams },
    {
      fallbackData: options.fallbackData,
      revalidateOnMount: options.revalidateOnMount
    }
  );
  return {
    key,
    ...infiniteResponse
  };
};

const Loader = () => <CourseCarouselSkeleton number={4} />;

const CoursesList = ({ authorID, filtersApplied }) => {
  const router = useRouter();
  const [activeFilters, setActiveFilters] = useState(filtersApplied.type);
  const { liveCourseList } = useLiveClassAndCourseList();

  const {
    data: filteredCoursesResponse,
    setSize,
    isValidating: isFetchingFilteredCourses
  } = useCoursesByFilters(
    { userName: authorID?.slice(1) },
    {
      filtersApplied: {
        ...filtersApplied,
        ...getFiltersApplied(router.query)
      }
    },
    { revalidateOnMount: false }
  );

  const formattedClassData = useMemo(() => {
    const courseResponseData = filteredCoursesResponse?.results || [];
    if (!isEmptyObject(liveCourseList)) {
      return courseResponseData.map((item) => ({
        ...item,
        isLive: !!liveCourseList[item.uid]
      }));
    }
    return courseResponseData;
  }, [filteredCoursesResponse?.results, liveCourseList]);

  useEffect(() => {
    const [type] = getParamsFromURL(window.location, ['type']);
    if (type !== activeFilters) setActiveFilters(type || 'popular');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    router.push(
      `/[authorID]/courses/?type=${activeFilters}`,
      `/${authorID}/courses/?type=${activeFilters}`,
      { shallow: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters]);

  const handleViewMore = () => {
    setSize((size) => size + 1);
  };

  const renderCourses = useCallback((courseInfo) => {
    return <StyledCourseCard course={courseInfo} />;
  }, []);

  const handleFilterChange = (option) => {
    setActiveFilters(option);
    setSize(0);
  };

  return (
    <Container>
      <FiltersTab
        filters={[
          { key: 'popular', label: 'Popular' },
          { key: 'latest', label: 'Latest' }
        ]}
        selectedFilter={activeFilters}
        handleChange={handleFilterChange}
      />
      <InnerContainer>
        {formattedClassData?.map((course) => {
          return renderCourses(course);
        })}
        {!isFetchingFilteredCourses && formattedClassData?.length === 0 && (
          <EducatorEmptyCourse
            header="No courses found"
            detail="The educator doesn’t have any courses. Select a different category or try with different filters. "
          />
        )}
        {!isFetchingFilteredCourses &&
        formattedClassData?.length < filteredCoursesResponse?.count ? (
          <StyledViewMoreButtonContainer onClick={handleViewMore}>
            <StyledViewMoreButton>
              <Typography variant="h6">View more</Typography>{' '}
              <StyledChevronWrapper>
                <BottomChevron height={16} width={16} />
              </StyledChevronWrapper>
            </StyledViewMoreButton>
          </StyledViewMoreButtonContainer>
        ) : (
          <></>
        )}
        {isFetchingFilteredCourses ? <Loader /> : <></>}
      </InnerContainer>
    </Container>
  );
};

export default CoursesList;
